import React, { useState } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { Spinner } from "@emisgroup/ui-progress-indicator";
import { useQueryClient } from "@tanstack/react-query";
import { Banner } from "@emisgroup/ui-banner";
import {
  ButtonGroup,
  Button,
  Dialog,
  DialogInner,
  DialogHeader,
  DialogContent,
  FormElement,
  Input
} from "../../../Common/UI/Index";
import { ButtonVariantType, InputType } from "../../../Common/UI/Types";
import { PracticeService } from "../../../../service/services/practice.service";
import { ScreenMode, ISiteConfiguration } from "./Types";
import { isValidatePostcode } from "./Scripts";
import "./Style.scss";
import InfoNotification from "~icons/ic/outline-info";

interface ISiteEntryDialogProps {
  mode: string;
  site: ISiteConfiguration;
  sites: ISiteConfiguration[];
  dsa: string;
  onCloseDialog: () => void;
  onSaveDialog: (site: ISiteConfiguration) => void;
}

const practiceService = new PracticeService();

export const SiteEntryDialog: React.FC<ISiteEntryDialogProps> = (
  { mode, site, sites, dsa, onCloseDialog, onSaveDialog  }
) => {
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [name, setName] = useState<string>(site.name);
  const [nameErrorText, setNameErrorText] = useState<string>("");
  const [postcodeErrorText, setPostcodeErrorText] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [postcode, setPostcode] = useState<string>(site.postcode);
  const [radius, setRadius] = useState<number>(site.radius);
  const [radiusErrorText, setRadiusErrorText] = useState<string>("");
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const onNameChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const value = e.target["value"];
    const errorText = value.length === 0 ? translate("MultiSiteList.SiteDialog.Name.ErrorText") : "";
    setNameErrorText(errorText );
    setName(value);
    setPostcodeErrorText("");
    setErrorMessage("");
  };

  const onPostcodeChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const value = e.target["value"];
    const errorText = isValidatePostcode(value) ? "" : translate("MultiSiteList.SiteDialog.Postcode.ErrorText");
    setPostcodeErrorText(errorText);
    setPostcode(value);
    setErrorMessage("");
  };

  const onRadiusChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const valueStr = e.target["value"];
    const value = parseFloat(valueStr);
    let errorText = "";
    const isInvalid =
      isNaN(value)
      || value < 0.1 || value > 100.0
      || (/^0\d+/.test(valueStr.trim()) && !/^0\.1$/.test(valueStr.trim()))
    ;

    if (isInvalid) {
      errorText = translate("MultiSiteList.SiteDialog.Radius.ErrorText");
    }

    setRadiusErrorText(errorText);
    setRadius(value);
    setErrorMessage("");
  };

  const onSaveClick = async (): Promise<void> => {
    setSaveLoading(true);

    const nameAlreadyExist = sites.find((item) =>
      item.name.toUpperCase() === name.trim().toUpperCase());
    const postcodeAlreadyExist = sites.find((item) =>
      item.postcode.toUpperCase() === postcode.trim().toUpperCase());
    if (mode === ScreenMode.EDIT && site.name !== name.trim() && nameAlreadyExist ||
        mode === ScreenMode.ADD && nameAlreadyExist) {
      setNameErrorText(translate("MultiSiteList.SiteDialog.Name.ErrorTextInvalidName"));
      setSaveLoading(false);
    } else if (mode === ScreenMode.EDIT && site.postcode !== postcode.trim() && postcodeAlreadyExist ||
        mode === ScreenMode.ADD && postcodeAlreadyExist) {
      setPostcodeErrorText(translate("MultiSiteList.SiteDialog.Postcode.ErrorTextInvalidPostcode"));
      setSaveLoading(false);
    } else {
      const abortController = new AbortController();
      const queryKey = [
        "practices-within-radius",
        postcode,
        radius,
        dsa
      ];

      try {
        const practices = await queryClient.fetchQuery({
          queryKey,
          queryFn: () =>
            practiceService.getPractices({
              postcode,
              radius: radius.toString(),
              dsa: dsa
            }, abortController.signal),
        });

        site.name = name.trim();
        site.postcode = postcode;
        site.radius = radius;
        site.practices = practices;
        setSaveLoading(false);
        onSaveDialog(site);
      } catch (error) {
        if (error && error["errors"] && Array.isArray(error["errors"]) && error["errors"].length > 0) {
          const errorMsg = error["errors"][0].message || translate("UnknownError");
          setErrorMessage(errorMsg);
        }
        setSaveLoading(false);
      }
    }
  };

  React.useEffect(() => {
    const saveDisabledState = name.length > 0 && isValidatePostcode(postcode) && !radiusErrorText;
    setSaveDisabled(!saveDisabledState);
  }, [name, postcode, radiusErrorText]);

  const handleDialogClose = (): void => {
    if (!saveLoading) {
      onCloseDialog();
    }
  };

  return (
    <div id="site-entry-dialog">
      <Dialog
        open={true}
        onOpenChange={handleDialogClose}
        preventBackdropClose={true}
      >
        <DialogInner>
          <DialogHeader>
            <div>
              {translate("MultiSiteList.SiteDialog.Title")}
            </div>
          </DialogHeader>
          <DialogContent>
            {errorMessage && (
              <Banner color="error">
                <Banner.Content>
                  <InfoNotification title="Error" />
                  <div className="site-dialog-error-message" dangerouslySetInnerHTML={{ __html: errorMessage }} />
                </Banner.Content>
              </Banner>
            )}
            {
              saveLoading
                ? (
                  <div className="site-dialog-loading">
                    <Spinner/>
                    <p>
                      {
                        translate("MultiSiteList.SiteDialog.LoadingMessage", {
                          postcode: postcode,
                          radius: radius
                        })
                      }</p>
                  </div>
                )
                : (
                  <div className="site-dialog-fields">
                    <FormElement
                      className="site-dialog-fields__name"
                      labelText={translate("MultiSiteList.SiteDialog.Name.Title")}
                      errorText={nameErrorText}
                      mandatory={true}>
                      <Input
                        type={InputType.Text}
                        name="SiteName"
                        required={true}
                        value={name}
                        maxLength={40}
                        onChange={onNameChange}
                      />
                    </FormElement>
                    <FormElement
                      className="site-dialog-fields__postcode"
                      labelText={translate("MultiSiteList.SiteDialog.Postcode.Title")}
                      errorText={postcodeErrorText}
                      mandatory={true}>
                      <Input
                        type={InputType.Text}
                        name="SitePostcode"
                        placeholder="LS19 6BA"
                        required={true}
                        value={postcode}
                        onChange={onPostcodeChange}
                      />
                    </FormElement>
                    <FormElement
                      className="site-dialog-fields__radius"
                      labelText={translate("MultiSiteList.SiteDialog.Radius.Title")}
                      errorText={radiusErrorText}
                      mandatory={true}>
                      <Input
                        type={InputType.Number}
                        name="SiteRadius"
                        required={true}
                        value={radius}
                        onChange={onRadiusChange}
                      />
                    </FormElement>
                  </div>
                )
            }

            <ButtonGroup className="site-view-container__buttons">
              <Button
                variant={ButtonVariantType.FILLED}
                onClick={onSaveClick}
                disabled={saveDisabled || saveLoading}>
                {translate("Button.Save")}
              </Button>
              <Button
                borderless={true}
                onClick={handleDialogClose}>
                {translate("Button.Cancel")}
              </Button>
            </ButtonGroup>
          </DialogContent>
        </DialogInner>
      </Dialog>
    </div>
  );
};
